import * as React from 'react'

import {
  CONTEXT_MENU_PANEL_WIDTH_PX,
  CONTEXT_MENU_VIEWPORT_PADDING_PX,
  type ContextMenuViewportRect,
  getWindowContextMenuViewport,
  resolveContextMenuViewportPosition,
} from '@/lib/editor/context-menu-viewport'

function readViewportBounds(
  viewportBoundsRef?: React.RefObject<HTMLElement | null>
): ContextMenuViewportRect {
  const el = viewportBoundsRef?.current

  if (el) {
    return el.getBoundingClientRect()
  }

  return getWindowContextMenuViewport()
}

/**
 * Keeps the open menu inside the viewport bounds (the canvas scroller when given, else the
 * window): flips/clamps its anchor and caps its height. Recomputes on open, on menu resize and
 * on window resize or scroll (window and scroller).
 */
export function useContextMenuPosition({
  open,
  position,
  viewportBoundsRef,
  menuRef,
}: {
  open: boolean
  position: { x: number; y: number }
  viewportBoundsRef?: React.RefObject<HTMLElement | null>
  menuRef: React.RefObject<HTMLDivElement | null>
}) {
  const [resolvedPosition, setResolvedPosition] = React.useState(() => ({
    left: position.x,
    top: position.y,
  }))
  const [maxHeight, setMaxHeight] = React.useState<number | undefined>(undefined)

  const recomputePosition = React.useCallback(() => {
    const menu = menuRef.current

    if (!menu) {
      setResolvedPosition({ left: position.x, top: position.y })
      return
    }

    const viewport = readViewportBounds(viewportBoundsRef)
    const padding = CONTEXT_MENU_VIEWPORT_PADDING_PX
    const width = menu.offsetWidth || CONTEXT_MENU_PANEL_WIDTH_PX
    // Use scrollHeight (natural content height) so the flip logic uses the real menu size,
    // not the CSS-capped height which would cause the menu to open below even when it doesn't fit.
    const naturalHeight = menu.scrollHeight

    const resolved = resolveContextMenuViewportPosition(
      position.x,
      position.y,
      width,
      naturalHeight,
      viewport
    )

    // Available vertical space from the resolved top to the viewport bottom edge.
    const availableHeight = viewport.bottom - resolved.top - padding

    setResolvedPosition(resolved)
    setMaxHeight(Math.max(0, availableHeight))
  }, [position.x, position.y, viewportBoundsRef, menuRef])

  React.useLayoutEffect(() => {
    if (!open) return
    recomputePosition()
  }, [open, recomputePosition])

  React.useEffect(() => {
    if (!open) return

    const menu = menuRef.current

    if (!menu || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      recomputePosition()
    })

    observer.observe(menu)

    return () => {
      observer.disconnect()
    }
  }, [open, recomputePosition, menuRef])

  React.useEffect(() => {
    if (!open) return

    const onViewportChange = () => {
      recomputePosition()
    }

    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)

    const scroller = viewportBoundsRef?.current

    scroller?.addEventListener('scroll', onViewportChange)

    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
      scroller?.removeEventListener('scroll', onViewportChange)
    }
  }, [open, recomputePosition, viewportBoundsRef])

  return { resolvedPosition, maxHeight }
}
